import { useMemo } from "react";
import {
  Box, Grid, Card, CardContent, Typography, LinearProgress, Chip, Stack, alpha,
} from "@mui/material";
import {
  TrendingUp, PendingActions, SmartToy, CycloneOutlined,
  ReportProblemOutlined,
} from "@mui/icons-material";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import { AgentAvatar } from "../components/AgentAvatar";
import { agentColor, splitAgentName, tint } from "../utils/agentDisplay";
import { useCycleFilter } from "../cycleFilterContext";
import { CycleStatusChart } from "../components/CycleStatusChart";
import { StageInsights } from "../components/dashboard/StageInsights";
import { MoneyFlowCard } from "../components/dashboard/MoneyFlowCard";
import { TypeBreakdownCard } from "../components/dashboard/TypeBreakdownCard";
import { InsightsRotator } from "../components/dashboard/InsightsRotator";
import { AiDisclaimer } from "../components/AiDisclaimer";

// Metric card accent colors come from theme CSS variables so they remain
// readable in both light and dark mode (light mode uses darker variants).
// glowColor is used purely for the box-shadow alpha and works in both modes.
const METRIC_CARDS = [
  {
    key: "queue",
    label: "Queue Depth",
    icon: <PendingActions />,
    color: "var(--c-metric-warning)",
    glowColor: "#fdcb6e",
    gradient: "linear-gradient(135deg, rgba(253,203,110,0.15), rgba(253,203,110,0.05))",
    borderColor: "rgba(253,203,110,0.25)",
  },
  {
    key: "pending",
    label: "Pending Review",
    icon: <TrendingUp />,
    color: "var(--c-metric-info)",
    glowColor: "#74b9ff",
    gradient: "linear-gradient(135deg, rgba(116,185,255,0.15), rgba(116,185,255,0.05))",
    borderColor: "rgba(116,185,255,0.25)",
  },
  {
    key: "agents",
    label: "Active Agents",
    icon: <SmartToy />,
    color: "var(--c-metric-success)",
    glowColor: "#55efc4",
    gradient: "linear-gradient(135deg, rgba(85,239,196,0.15), rgba(85,239,196,0.05))",
    borderColor: "rgba(85,239,196,0.25)",
  },
  {
    key: "status",
    label: "Cycle Status",
    icon: <CycloneOutlined />,
    color: "var(--c-metric-primary)",
    glowColor: "#a29bfe",
    gradient: "linear-gradient(135deg, rgba(162,155,254,0.15), rgba(162,155,254,0.05))",
    borderColor: "rgba(162,155,254,0.25)",
  },
];

const statusColor: Record<string, "success" | "primary" | "error" | "default" | "warning"> = {
  idle: "success",
  processing: "primary",
  error: "error",
  offline: "default",
};

export function Dashboard() {
  // Multi-active cycles: read from the global cycle filter (top bar dropdown).
  // When "All Cycles" is selected, no reconciliationCycleId is sent — server
  // falls back to its default resolver (most recent non-archived cycle).
  const { isAll, selectedCycleId } = useCycleFilter();
  const overview = useStableQuery(
    api.dashboard.getOverview,
    isAll ? {} : { reconciliationCycleId: selectedCycleId as any },
    { cycle: null, agents: [], queueDepth: 0, pendingReviewCount: 0 },
  );

  // W5.3/W5.4 — Rules of Hooks: all `useStableQuery` calls MUST be unconditional
  // and happen BEFORE any early return. Previously these were placed after the
  // `overview === undefined` guard which caused React error #310 ("rendered
  // more hooks than during the previous render") on first paint.
  const driftStats = useStableQuery(
    api.dashboard.getQbDriftStats,
    {},
    { currentDriftCount: 0, driftedLast7Days: 0, lastDriftAt: null as number | null },
  );

  // v3.0.71 — `useStableQuery` returns `undefined` during the 6s pre-timeout
  // window (its fallback only kicks in once the timeout fires). The JSX
  // below dereferences many properties without optional-chaining; harden
  // here once so every read site can stay tidy.
  const driftCount = driftStats?.currentDriftCount ?? 0;
  const driftLast7 = driftStats?.driftedLast7Days ?? 0;
  const driftLastAt = driftStats?.lastDriftAt ?? null;

  // When "All Cycles" is selected, aggregate stats across all non-archived
  // cycles so the hero card shows totals instead of the most-recent cycle.
  const allCycleBreakdown = useStableQuery(
    api.dashboardCharts.getCycleStatusBreakdown,
    {},
    [] as any[],
  ) as any[] | undefined;

  const aggregatedCycle = useMemo(() => {
    if (!isAll || !allCycleBreakdown || allCycleBreakdown.length === 0) return null;
    let totalTransactions = 0;
    let matchedCount = 0;
    let humanReviewCount = 0;
    let unmatchedCount = 0;
    let postedCount = 0;
    for (const c of allCycleBreakdown) {
      totalTransactions += c.total ?? 0;
      matchedCount += c.matched ?? 0;
      humanReviewCount += c.humanReview ?? 0;
      // v3.0.43: matchStatus="exception" was retired (merged into
      // "unmatched"). Pre-fix data may still report a non-zero exception
      // bucket from the cycleStatusChart breakdown; fold it into the
      // unmatched count so the dashboard rolls up consistently.
      unmatchedCount += (c.unmatched ?? 0) + (c.exception ?? 0);
      postedCount += c.posted ?? 0;
    }
    const reconciliationRate = totalTransactions > 0
      ? Math.round(((matchedCount + humanReviewCount) / totalTransactions) * 100)
      : 0;
    return {
      name: "All Cycles",
      status: "active",
      totalTransactions,
      matchedCount,
      humanReviewCount,
      unmatchedCount,
      postedCount,
      reconciliationRate,
    };
  }, [isAll, allCycleBreakdown]);

  if (overview === undefined) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Reconciliation Dashboard</Typography>
        <LinearProgress sx={{ height: 3, borderRadius: 2, mt: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading data...
        </Typography>
      </Box>
    );
  }

  const { agents, queueDepth, pendingReviewCount } = overview;
  // When "All Cycles", use aggregated data; otherwise use the single cycle
  const cycle = (isAll && aggregatedCycle) ? aggregatedCycle : overview.cycle;
  const activeAgents = agents.filter((a: any) => a.status !== "offline").length;

  const metricValues: Record<string, React.ReactNode> = {
    queue: queueDepth,
    pending: pendingReviewCount,
    agents: `${activeAgents}/${agents.length}`,
    status: cycle ? (
      <Chip
        label={cycle.status}
        size="small"
        color={cycle.status === "completed" ? "success" : cycle.status === "active" ? "primary" : "default"}
        sx={{ fontWeight: 700 }}
      />
    ) : (
      <Typography variant="body2" color="text.secondary">No cycle</Typography>
    ),
  };

  return (
    <Box>
      <AiDisclaimer context="These figures are derived from AI-processed data and can contain errors" />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box data-tour="page-title-dashboard">
          <Typography variant="h4">Reconciliation Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time overview of your financial reconciliation pipeline
          </Typography>
        </Box>
      </Stack>

      {cycle ? (
        <Grid container spacing={2.5}>
          {/* Reconciliation Rate — Hero Card */}
          <Grid item xs={12} data-tour="hero-card">
            <Card
              sx={{
                background: "linear-gradient(135deg, rgba(108,92,231,0.08), rgba(0,206,201,0.04))",
                border: "1px solid rgba(108,92,231,0.15)",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: "linear-gradient(90deg, #6c5ce7, #00cec9)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{cycle.name}</Typography>
                    <Typography variant="body2" color="text.secondary">Active Reconciliation Cycle</Typography>
                  </Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 900,
                      background: "linear-gradient(135deg, var(--c-metric-primary), var(--c-metric-success))",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {cycle.reconciliationRate}%
                  </Typography>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={cycle.reconciliationRate}
                  sx={{ height: 8, borderRadius: 4, mb: 2 }}
                />

                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                  <Chip label={`${cycle.totalTransactions} Total`} variant="outlined" size="small" />
                  <Chip label={`${cycle.matchedCount} Matched`} color="success" size="small" />
                  {(cycle as any).humanReviewCount > 0 && (
                    <Chip label={`${(cycle as any).humanReviewCount} In Review`} color="info" size="small" />
                  )}
                  <Chip label={`${cycle.unmatchedCount} Unmatched`} color="warning" size="small" />
                  {(cycle as any).postedCount > 0 && (
                    <Chip label={`${(cycle as any).postedCount} Posted to QB`} color="secondary" size="small" />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* v3.0.61 — Smart analytics row: Money flow + Posted by type + Insights */}
          <Grid item xs={12} md={6} lg={4} data-tour="card-money-flow">
            <MoneyFlowCard />
          </Grid>
          <Grid item xs={12} md={6} lg={4} data-tour="card-type-breakdown">
            <TypeBreakdownCard />
          </Grid>
          <Grid item xs={12} lg={4} data-tour="card-insights">
            <InsightsRotator />
          </Grid>

          {/* Cross-cycle Reconciliation Trend Chart */}
          <Grid item xs={12} data-tour="trend-chart">
            <CycleStatusChart />
          </Grid>

          {/* Stage distribution over time (drill-down) + open-work donut, shared selection */}
          <Grid item xs={12}>
            <StageInsights />
          </Grid>

          {/* W5.3 — Duplicate detection card removed 2026-05-27: the raw
              "Duplicates Blocked (30d)" count confused the client on the
              dashboard (it spiked from reprocess churn, not real recurring
              dupes). Duplicate-detection stats remain queryable via the
              Teams orchestrator chat. The /duplicates page still lists the
              detailed candidates; getDuplicateDetectionStats stays for the
              orchestrator. */}

          {/* W5.4 — QB drift rate card (amber when > 0) */}
          <Grid item xs={12} sm={6} md={3} data-tour="card-drift">
            <Card
              sx={{
                background:
                  driftCount > 0
                    ? "linear-gradient(135deg, rgba(253,203,110,0.18), rgba(253,203,110,0.06))"
                    : "linear-gradient(135deg, rgba(85,239,196,0.12), rgba(85,239,196,0.04))",
                border:
                  driftCount > 0
                    ? "1px solid rgba(253,203,110,0.35)"
                    : "1px solid rgba(85,239,196,0.22)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 24px ${alpha(
                    driftCount > 0 ? "#fdcb6e" : "#55efc4",
                    0.15,
                  )}`,
                },
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "1.5px",
                      color: "var(--c-text-secondary)",
                      fontSize: "0.65rem",
                    }}
                  >
                    QB Drift Detected
                  </Typography>
                  <Box
                    sx={{
                      color:
                        driftCount > 0
                          ? "var(--c-metric-warning)"
                          : "var(--c-metric-success)",
                      opacity: 0.7,
                    }}
                  >
                    <ReportProblemOutlined />
                  </Box>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color:
                      driftCount > 0
                        ? "var(--c-metric-warning)"
                        : "var(--c-metric-success)",
                  }}
                >
                  {driftCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {driftLast7} new (7d)
                  {driftLastAt
                    ? ` · last ${new Date(driftLastAt).toLocaleDateString()}`
                    : ""}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Metric Cards */}
          {METRIC_CARDS.map((card, idx) => (
            <Grid item xs={12} sm={6} md={3} key={card.key} {...(idx === 0 ? { "data-tour": "metric-cards" } : {})}>
              <Card
                sx={{
                  background: card.gradient,
                  border: `1px solid ${card.borderColor}`,
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 24px ${alpha(card.glowColor, 0.15)}`,
                  },
                }}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "1.5px",
                        color: "var(--c-text-secondary)",
                        fontSize: "0.65rem",
                      }}
                    >
                      {card.label}
                    </Typography>
                    <Box sx={{ color: card.color, opacity: 0.7 }}>{card.icon}</Box>
                  </Stack>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, color: card.color }}
                  >
                    {metricValues[card.key]}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {/* Agent Status Grid */}
          <Grid item xs={12} data-tour="agent-grid">
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                  <Typography variant="h6">Agent Status</Typography>
                  <Chip
                    label={`${activeAgents}/${agents.length} Online`}
                    color={activeAgents === agents.length ? "success" : "warning"}
                    size="small"
                  />
                </Stack>
                <Grid container spacing={2}>
                  {agents.map((agent: any) => {
                    const c = agentColor(agent.agentId);
                    const { name, role } = splitAgentName(agent.agentName);
                    return (
                    <Grid item xs={12} sm={6} md={4} key={agent.agentId}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2.5,
                          background: `linear-gradient(135deg, ${tint(c, 0.14)} 0%, ${tint(c, 0.04)} 100%)`,
                          border: `1px solid ${tint(c, 0.35)}`,
                          borderLeft: `4px solid ${c}`,
                          transition: "border-color 0.2s ease, transform 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
                          <AgentAvatar agentId={agent.agentId} size={36} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{name}</Typography>
                            {role && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.65rem" }}>
                                {role}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            size="small"
                            label={agent.status}
                            color={statusColor[agent.status] ?? "default"}
                            sx={{ height: 22, fontSize: "0.65rem" }}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                          Completed: {agent.tasksCompleted} &middot; Failed: {agent.tasksFailed}
                        </Typography>
                        {agent.currentTask && (
                          <Typography variant="caption" sx={{ color: "var(--c-link-accent)", mt: 0.5, display: "block" }}>
                            {agent.currentTask}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Card
          sx={{
            background: "linear-gradient(135deg, rgba(108,92,231,0.05), rgba(0,206,201,0.03))",
            border: "1px solid rgba(108,92,231,0.1)",
            textAlign: "center",
            py: 6,
          }}
        >
          <CardContent>
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2,
                borderRadius: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(108,92,231,0.1)",
                border: "1px solid rgba(108,92,231,0.2)",
              }}
            >
              <CycloneOutlined sx={{ fontSize: 32, color: "var(--c-link-accent)" }} />
            </Box>
            <Typography variant="h6" sx={{ mb: 1 }}>No Active Cycle</Typography>
            <Typography color="text.secondary">
              Create a new reconciliation cycle to get started.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
